import { Platform } from 'react-native';
import { readWebStorage, writeWebStorage } from './web-storage';
import {
  isThemeMode,
  TABBIO_THEME_MODE_STORAGE_KEY,
  type ThemeMode,
} from './theme-mode';

export type SupportedLocale = 'en' | 'ar';

export const LOCALE_STORAGE_KEY = 'tabbio-locale';

export function normalizeLocale(value?: string | null): SupportedLocale | null {
  if (value === 'en' || value === 'ar') {
    return value;
  }

  return null;
}

function getNativeSecureStore():
  | typeof import('expo-secure-store')
  | null {
  if (Platform.OS === 'web') {
    return null;
  }

  try {
    return require('expo-secure-store') as typeof import('expo-secure-store');
  } catch {
    return null;
  }
}

function readNativeSecureStoreSync(key: string) {
  try {
    const SecureStore = getNativeSecureStore();
    return SecureStore?.getItem?.(key) ?? null;
  } catch {
    return null;
  }
}

async function readNativeSecureStoreAsync(key: string) {
  try {
    const SecureStore = getNativeSecureStore();
    return (await SecureStore?.getItemAsync?.(key)) ?? null;
  } catch {
    return null;
  }
}

function writeNativeSecureStore(key: string, value: string) {
  try {
    const SecureStore = getNativeSecureStore();
    if (SecureStore?.setItemAsync) {
      void SecureStore.setItemAsync(key, value).catch(() => undefined);
    }
  } catch {
    // Native preference persistence should never block the visible selection.
  }
}

/**
 * Read a simple string preference from wherever this platform keeps them:
 * `localStorage` on web, SecureStore on native. Synchronous on both, so it can
 * seed a `useSyncExternalStore` snapshot without a loading state.
 */
export function readPersistedPreference(key: string): string | null {
  if (Platform.OS === 'web') {
    return readWebStorage(key);
  }

  return readNativeSecureStoreSync(key);
}

/**
 * Write one back. Fire-and-forget on native (SecureStore's setter is async):
 * the caller has already moved its own state, so a slow keychain write must
 * never hold up the UI.
 */
export function persistPreference(key: string, value: string) {
  if (Platform.OS === 'web') {
    writeWebStorage(key, value);
    return;
  }

  writeNativeSecureStore(key, value);
}

export function getPersistedThemeMode(): ThemeMode {
  const stored = readPersistedPreference(TABBIO_THEME_MODE_STORAGE_KEY);
  return isThemeMode(stored) ? stored : 'system';
}

export function persistThemeMode(mode: ThemeMode) {
  persistPreference(TABBIO_THEME_MODE_STORAGE_KEY, mode);
}

export function getPersistedLocaleSync(): SupportedLocale | null {
  return normalizeLocale(readPersistedPreference(LOCALE_STORAGE_KEY));
}

export async function getPersistedLocaleAsync(): Promise<SupportedLocale | null> {
  if (Platform.OS === 'web') {
    return getPersistedLocaleSync();
  }

  return normalizeLocale(await readNativeSecureStoreAsync(LOCALE_STORAGE_KEY));
}

export async function persistLocalePreference(locale: SupportedLocale) {
  if (Platform.OS === 'web') {
    writeWebStorage(LOCALE_STORAGE_KEY, locale);
    return;
  }

  try {
    const SecureStore = getNativeSecureStore();
    await SecureStore?.setItemAsync?.(LOCALE_STORAGE_KEY, locale);
  } catch {
    // SecureStore is unavailable in tests and some native recovery paths.
  }
}
